// src/PayrollDashboard.js - VERSIONE COMPLETA CON TUTTE LE FUNZIONALITÀ

import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, orderBy, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { useUser } from './UserContext';
import moment from 'moment';
import { toast } from 'react-toastify';
import './PayrollDashboard.css';
import DoctorsTally from './DoctorsTally';
import './LeaveManagement.css';
// Aggiungi queste importazioni in alto nel file se mancano
import { FaUndo, FaRedo, FaFilter, FaCheck, FaMagic, FaSave, FaExclamationTriangle, FaEyeSlash } from 'react-icons/fa';

// Riferimenti alle Cloud Functions
const calculateMonthlyPayrollFn = httpsCallable(functions, 'calculateMonthlyPayrollData');
const exportFullReportFn = httpsCallable(functions, 'exportFullPayrollReport');

// === COMPONENTE MODAL DETTAGLIO DIPENDENTE ===
function EmployeeDetailModal({ summary, onClose, onSave, previousMonthData }) {
  const [localNotes, setLocalNotes] = useState(summary.notes || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(summary.id, { notes: localNotes });
      toast.success('Note salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  // Statistiche Generali
  const totalDays = summary.dailyBreakdown?.length || 0;
  
  // Calcolo alert reali (escludendo festivi/chiusi)
  const trueAlertsCount = summary.dailyBreakdown?.filter(d => {
    const isHolidayOrZero = ["CHIUSO", "Festivo", "FESTIVO", "Riposo"].includes(d.shiftName) || d.expectedMinutes === 0;
    return d.hasMissingTimelogs && !isHolidayOrZero;
  }).length || 0;

  const avgHoursPerDay = totalDays > 0 ? (summary.totalWorkedHours / totalDays).toFixed(2) : 0;

  // Confronto mese precedente
  const comparison = previousMonthData ? {
    prevHours: previousMonthData.totalWorkedHours || 0,
    diff: (summary.totalWorkedHours || 0) - (previousMonthData.totalWorkedHours || 0)
  } : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content full-width-modal" onClick={(e) => e.stopPropagation()}> 
        {/* Nota: la classe .full-width-modal è gestita nel CSS */}
        
        {/* Header - ORA FISSO E ALLINEATO */}
        <div className="modal-header">
          <div className="header-title-row">
            {/* Nome e Cognome */}
            <h2>{summary.userName}</h2>
            
            {/* Badges sulla stessa riga */}
            <span className={`assignment-badge ${summary.assignmentType}`}>
              {summary.assignmentType === 'backoffice' ? '🏢 Backoffice' : '🔄 Turnista'}
            </span>
            <span className={`status-badge-modal ${summary.status || 'calculated'}`}>
              {(summary.status === 'reviewed' && '👁️ Revisionato') ||
               (summary.status === 'approved' && '✅ Approvato') ||
               '📊 Calcolato'}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Statistiche Cards */}
        <div className="modal-stats">
          <div className="stat-card">
            <div className="stat-label">Giorni Lavorati</div>
            <div className="stat-value">{totalDays}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Media Ore/Giorno</div>
            <div className="stat-value">{avgHoursPerDay}h</div>
          </div>
          
          {/* Card Alert Timbrature */}
          <div className={`stat-card ${trueAlertsCount > 0 ? 'warning' : 'success'}`}>
            <div className="stat-label">
                {trueAlertsCount > 0 ? '⚠️ Alert Timbrature' : '✅ Timbrature'}
            </div>
            <div className="stat-value">
                {trueAlertsCount > 0 ? trueAlertsCount : 'OK'}
            </div>
          </div>

          {/* Card Eccedenza */}
          <div className={`stat-card ${summary.excessHours > 5 ? 'info' : ''}`} 
               style={summary.excessHours > 5 ? {backgroundColor: '#fff3cd', color: '#856404'} : {}}>
            <div className="stat-label">⏱️ Eccedenza</div>
            <div className="stat-value">
                {summary.excessHours > 0 ? '+' : ''}{summary.excessHours?.toFixed(2)}h
            </div>
          </div>
        </div>

        {/* Alert Boxes */}
        {summary.missingTimelogsAlerts?.length > 0 && (
            <MissingTimelogsAlert alerts={summary.missingTimelogsAlerts} />
        )}
        {summary.excessHours > 5 && (
            <div style={{marginTop: '15px'}}>
               <ExcessHoursAlert excessHours={summary.excessHours} />
            </div>
        )}

        {/* Riepilogo Mensile */}
        <div className="modal-summary">
          <h3>📊 Riepilogo Mensile</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="label">Ore Ordinarie:</span>
              <span className="value">{summary.totalWorkedHours?.toFixed(2)}h</span>
            </div>
            <div className="summary-item">
              <span className="label">Ore Eccedenza:</span>
              <span className="value excess">{summary.excessHours?.toFixed(2)}h</span>
            </div>
            <div className="summary-item">
              <span className="label">Ore Straordinario:</span>
              <span className="value">{summary.overtimeHours?.toFixed(2)}h</span>
            </div>
            <div className="summary-item">
              <span className="label">Ferie:</span>
              <span className="value">{summary.leaveDays?.Ferie || 0} gg</span>
            </div>
            <div className="summary-item">
              <span className="label">ROL:</span>
              <span className="value">{summary.leaveDays?.ROL?.toFixed(2) || 0}h</span>
            </div>
            <div className="summary-item">
              <span className="label">Permesso 104:</span>
              <span className="value">{summary.leaveDays?.Permesso104?.toFixed(2) || 0}h</span>
            </div>
            <div className="summary-item">
              <span className="label">Malattia:</span>
              <span className="value">{summary.sicknessDays || 0} gg</span>
            </div>
            {/* 🆕 Residuo attuale del dipendente, non del mese calcolato */}
            <div className="summary-item">
              <span className="label">Residuo Ferie:</span>
              <span className="value" style={{ color: (summary.vacationRemaining ?? 0) < 0 ? '#d32f2f' : 'inherit' }}>
                {summary.vacationRemaining != null ? summary.vacationRemaining.toFixed(2) : '-'} gg
              </span>
            </div>
            <div className="summary-item">
              <span className="label">Residuo ROL:</span>
              <span className="value" style={{ color: (summary.rolRemaining ?? 0) < 0 ? '#d32f2f' : 'inherit' }}>
                {summary.rolRemaining != null ? summary.rolRemaining.toFixed(2) : '-'} h
              </span>
            </div>
          </div>
        </div>

        {/* TABELLA GIORNALIERA */}
        {summary.dailyBreakdown && summary.dailyBreakdown.length > 0 && (
          <div className="modal-daily-breakdown">
            <h3>📅 Dettaglio Giornaliero</h3>
            <div className="daily-table-container">
              <table className="daily-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Turno</th>
                    <th>Entrata</th>
                    <th>Uscita</th>
                    <th>Previste</th>
                    <th>Straord.</th>
                    <th>ROL</th>
                    <th>104</th>
                    <th>Lavorate</th>
                    <th>Diff</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.dailyBreakdown.map((day, idx) => {
                    const diff = day.actualMinutes - day.expectedMinutes;
                    
                    const isHolidayOrZeroHours = 
                        ["CHIUSO", "Festivo", "FESTIVO", "Riposo", "Ferie", "Malattia"].includes(day.shiftName) || 
                        day.expectedMinutes === 0;

                    const showRowAlert = day.hasMissingTimelogs && !isHolidayOrZeroHours;
                    
                    const isLeaveDay = ["Ferie", "Malattia", "Permesso 104"].some(t => day.shiftName?.includes(t));
                    const shiftStyle = isLeaveDay ? {fontWeight: 'bold', color: '#1976d2'} : {};

                    return (
                      <tr key={idx} className={showRowAlert ? 'alert-row' : ''}>
                        <td>{moment(day.date).format('DD/MM')}</td>
                        <td style={shiftStyle}>{day.shiftName}</td>
                        <td className="time-cell">{day.startTime || '-'}</td>
                        <td className="time-cell">{day.endTime || '-'}</td>
                        <td>{(day.expectedMinutes / 60).toFixed(2)}h</td>
                        <td style={{color: day.overtimeMinutes > 0 ? '#2e7d32' : 'inherit', fontWeight: day.overtimeMinutes > 0 ? 'bold' : 'normal'}}>
                            {day.overtimeMinutes > 0 ? `+${(day.overtimeMinutes / 60).toFixed(2)}` : '-'}
                        </td>
                        <td>
                             {day.rolMinutes > 0 ? <span style={{color: '#e67e22', fontWeight: 'bold'}}>{(day.rolMinutes/60).toFixed(2)}h</span> : '-'}
                        </td>
                        <td>
                             {day.p104Minutes > 0 ? <span style={{color: '#9c27b0', fontWeight: 'bold'}}>{(day.p104Minutes/60).toFixed(2)}h</span> : '-'}
                        </td>
                        <td>{(day.actualMinutes / 60).toFixed(2)}h</td>
                        <td className={diff > 0 ? 'positive' : diff < 0 ? 'negative' : ''}>
                          {diff > 0 ? '+' : ''}{(diff / 60).toFixed(2)}h
                        </td>
                        <td>
                          {day.hasMissingTimelogs ? (
                             isHolidayOrZeroHours ? (
                                <span className="badge success">✓ {['Ferie', 'Malattia', 'Festivo', 'FESTIVO'].includes(day.shiftName) ? day.shiftName : 'OK'}</span>
                             ) : (
                                <span className="badge warning">⚠️ Presunto</span>
                             )
                          ) : (
                            <span className="badge success">✓ OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Note e Footer */}
        <div className="modal-notes">
          <h3>📝 Note Amministrative</h3>
          <textarea
            value={localNotes}
            onChange={(e) => setLocalNotes(e.target.value)}
            placeholder="Aggiungi note..."
            rows={4}
          />
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Chiudi</button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Salvataggio...' : 'Salva Note'}
          </button>
        </div>
      </div>
    </div>
  );
}

// === COMPONENTE ALERT ===
function MissingTimelogsAlert({ alerts }) {
  if (!alerts || alerts.length === 0) return null;

  const workedDays = alerts.filter(a => !["CHIUSO", "Festivo", "FESTIVO"].includes(a.shiftName));
  const closedDays = alerts.filter(a => ["CHIUSO", "Festivo", "FESTIVO"].includes(a.shiftName));

  return (
    <div className="alert-section warning">
      <div className="alert-header">
        <span className="alert-icon">⚠️</span>
        <h4>Giorni senza timbrature ({alerts.length})</h4>
      </div>
      
      {workedDays.length > 0 && (
        <>
          <p className="alert-description critical">
            <strong>⚠️ ATTENZIONE:</strong> Giorni lavorativi senza timbrature.
            Ore calcolate come "presunte".
          </p>
          <ul className="alert-list critical">
            {workedDays.map((alert, idx) => (
              <li key={idx} className="alert-item">
                <span className="alert-date">{moment(alert.date).format('DD/MM/YYYY')}</span>
                <span className="alert-shift">{alert.shiftName}</span>
                <span className="alert-hours presumed">{alert.presumedHours}h <em>(presunte)</em></span>
              </li>
            ))}
          </ul>
        </>
      )}

      {closedDays.length > 0 && (
        <>
          <p className="alert-description info">ℹ️ Giorni di chiusura/festività:</p>
          <ul className="alert-list info">
            {closedDays.map((alert, idx) => (
              <li key={idx} className="alert-item info-item">
                <span className="alert-date">{moment(alert.date).format('DD/MM/YYYY')}</span>
                <span className="alert-shift">{alert.shiftName}</span>
                <span className="alert-hours info-hours">Nessuna timbratura necessaria</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// === NUOVO COMPONENTE: Box Eccedenza Ore ===
function ExcessHoursAlert({ excessHours }) {
  if (!excessHours || excessHours <= 5) return null; // Mostra solo se > 5 ore

  return (
    <div className="alert-section info" style={{backgroundColor: '#fff3cd', borderColor: '#ffeeba', color: '#856404'}}>
      <div className="alert-header">
        <span className="alert-icon">⏱️</span>
        <h4>Eccedenza Ore Rilevata</h4>
      </div>
      <p className="alert-description">
        <strong>Attenzione:</strong> Il dipendente ha accumulato <strong>{excessHours.toFixed(2)}h</strong> di eccedenza questo mese (soglia avviso: 5h).
        Verifica se è necessario pianificare un recupero o autorizzare lo straordinario.
      </p>
    </div>
  );
}

// === COMPONENTE PRINCIPALE ===
function PayrollDashboard() {
    const { userProfile } = useUser();
    const payrollPermission = userProfile?.permissions?.payroll || 'none';
    const canViewPayroll = payrollPermission === 'completo' || payrollPermission === 'semi-completo';
    const canViewDoctorsTally = canViewPayroll;
    
    const getDefaultTab = () => canViewPayroll ? 'summary' : canViewDoctorsTally ? 'doctors' : 'summary';
    
    const [activeTab, setActiveTab] = useState(getDefaultTab);
    const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'month'));
    const queryClient = useQueryClient();
    const [isCalculating, setIsCalculating] = useState(false);
    const [isExportingReport, setIsExportingReport] = useState(false);
    const [editedCosts, setEditedCosts] = useState({});
    const [expandedAlerts, setExpandedAlerts] = useState({});
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const monthStr = selectedMonth.format('YYYY-MM');
    const prevMonthStr = selectedMonth.clone().subtract(1, 'month').format('YYYY-MM');

    // Carica riepiloghi mese corrente (Cachati)
    const { data: summaries = [], isLoading: isLoadingCurrent } = useQuery({
        queryKey: ['monthlySummaries', userProfile?.centerId, monthStr],
        queryFn: async () => {
            const q = query(
                collection(db, 'centers', userProfile.centerId, 'monthlySummaries'),
                where('monthYear', '==', monthStr)
            );
            const snapshot = await getDocs(q);
            const summariesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const enriched = await Promise.all(
                summariesData.map(async (summary) => {
                    try {
                        const staffDoc = await getDoc(doc(db, 'centers', userProfile.centerId, 'staff', summary.userId));
                        // 🆕 'vacationRemaining'/'rolRemaining' NON si leggono più da qui:
                        // sono uno scatto fotografico già congelato dentro 'summary'
                        // (calcolato una volta per tutte da calculateMonthlyPayrollData
                        // e salvato su monthlySummaries). Qui serve solo l'assignmentType.
                        return { ...summary, assignmentType: staffDoc.data()?.assignmentType || 'rotation' };
                    } catch (error) {
                        return { ...summary, assignmentType: 'rotation' };
                    }
                })
            );

            return enriched.sort((a, b) => {
                if (a.assignmentType !== b.assignmentType) return a.assignmentType === 'backoffice' ? -1 : 1;
                return a.userName.localeCompare(b.userName);
            });
        },
        enabled: !!userProfile?.centerId
    });

    // Carica mese precedente per confronto (Cachati)
    const { data: previousMonthSummaries = [], isLoading: isLoadingPrev } = useQuery({
        queryKey: ['monthlySummaries', userProfile?.centerId, prevMonthStr],
        queryFn: async () => {
            const q = query(
                collection(db, 'centers', userProfile.centerId, 'monthlySummaries'),
                where('monthYear', '==', prevMonthStr)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        },
        enabled: !!userProfile?.centerId
    });

    const isLoading = isLoadingCurrent || isLoadingPrev;

    // Semaforo validazione
    const getValidationStatus = (summary) => {
        const hasAlerts = summary.missingTimelogsAlerts?.length > 0;
        const excessHours = summary.excessHours || 0;

        if (excessHours > 10 || (hasAlerts && summary.missingTimelogsAlerts.length > 10)) {
            return { icon: '🔴', label: 'Critico', class: 'critical' };
        } else if (excessHours > 5 || hasAlerts) {
            return { icon: '🟡', label: 'Attenzione', class: 'warning' };
        }
        return { icon: '🟢', label: 'OK', class: 'success' };
    };

    // Confronto storico
    const getComparison = (currentSummary) => {
        const prev = previousMonthSummaries.find(s => s.userId === currentSummary.userId);
        if (!prev) return null;

        const diff = currentSummary.totalWorkedHours - prev.totalWorkedHours;
        return {
            prevHours: prev.totalWorkedHours,
            diff: diff,
            percentage: ((diff / prev.totalWorkedHours) * 100).toFixed(1)
        };
    };

    const toggleAlerts = (userId) => {
        setExpandedAlerts(prev => ({ ...prev, [userId]: !prev[userId] }));
    };

    const handleMonthChange = (increment) => {
        setSelectedMonth(prev => prev.clone().add(increment, 'month'));
    };

    const handleCalculateSummaries = async () => {
        if (payrollPermission !== 'completo') {
            toast.warn("Non hai i permessi.");
            return;
        }
        const monthYear = selectedMonth.format('YYYY-MM');
        if (!window.confirm(`Ricalcolare ${selectedMonth.format('MMMM YYYY')}?`)) return;
        
        setIsCalculating(true);
        try {
            const result = await calculateMonthlyPayrollFn({ monthYear, centerId: userProfile.centerId });
            toast.success(result.data.message);
            queryClient.invalidateQueries({ queryKey: ['monthlySummaries', userProfile.centerId, monthYear] });
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Errore.");
        } finally {
            setIsCalculating(false);
        }
    };

    const downloadCSV = (csvData, fileName) => {
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportFullReport = async () => {
        setIsExportingReport(true);
        const monthYear = selectedMonth.format('YYYY-MM');
        try {
            const result = await exportFullReportFn({ monthYear, centerId: userProfile.centerId });
            if (result.data?.success && result.data.csvData) {
                downloadCSV(result.data.csvData, `Report_${monthYear}.csv`);
                toast.success("Report esportato!");
            } else {
                throw new Error("Nessun dato CSV.");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Errore.");
        } finally {
            setIsExportingReport(false);
        }
    };

    const handleCostChange = (summaryId, newValue) => {
        setEditedCosts(prev => ({ ...prev, [summaryId]: newValue }));
    };

    const handleSaveRow = async (summary) => {
        const newCost = editedCosts[summary.id];
        if (newCost === undefined) return;

        try {
            const docRef = doc(db, 'centers', userProfile.centerId, 'monthlySummaries', summary.id);
            await updateDoc(docRef, { realCost: parseFloat(newCost) });
            setEditedCosts(prev => {
                const newState = { ...prev };
                delete newState[summary.id];
                return newState;
            });
            toast.success("Costo aggiornato!");
            queryClient.invalidateQueries({ queryKey: ['monthlySummaries', userProfile.centerId, monthStr] });
        } catch (error) {
            console.error(error);
            toast.error("Errore.");
        }
    };

    const handleStatusChange = async (summaryId, newStatus) => {
        try {
            const docRef = doc(db, 'centers', userProfile.centerId, 'monthlySummaries', summaryId);
            await updateDoc(docRef, { status: newStatus });
            toast.success(`Stato: ${newStatus}`);
            queryClient.invalidateQueries({ queryKey: ['monthlySummaries', userProfile.centerId, monthStr] });
        } catch (error) {
            toast.error('Errore');
        }
    };

    const handleSaveNotes = async (summaryId, updates) => {
        const docRef = doc(db, 'centers', userProfile.centerId, 'monthlySummaries', summaryId);
        await updateDoc(docRef, updates);
        queryClient.invalidateQueries({ queryKey: ['monthlySummaries', userProfile.centerId, monthStr] });
    };

    const anyLoading = isCalculating || isExportingReport || isLoading;

    return (
        <div className="payroll-container">
            <div className="payroll-header">
                <h1>Gestione Paghe</h1>
                <p>Calcola riepiloghi mensili e gestisci i conteggi.</p>
            </div>
            
            <div className="tabs-nav">
                {canViewPayroll && (
                    <button onClick={() => setActiveTab('summary')} className={activeTab === 'summary' ? 'active' : ''}>
                        Riepilogo Mensile
                    </button>
                )}
                {canViewDoctorsTally && (
                    <button onClick={() => setActiveTab('doctors')} className={activeTab === 'doctors' ? 'active' : ''}>
                        Conteggio Medici
                    </button>
                )}
            </div>

            {activeTab === 'summary' && (
                <>
                    <div className="payroll-controls">
                        <div className="month-selector">
                            <button onClick={() => handleMonthChange(-1)} disabled={anyLoading}>‹ Prec.</button>
                            <span className="month-display">{selectedMonth.format('MMMM YYYY')}</span>
                            <button onClick={() => handleMonthChange(1)} disabled={anyLoading}>Succ. ›</button>
                        </div>

                        <div className="action-buttons">
                            <button className="calculate-btn" onClick={handleCalculateSummaries} 
                                disabled={payrollPermission !== 'completo' || anyLoading}>
                                {isCalculating ? 'Calcolo...' : 'Calcola Mese'}
                            </button>
                            <button className="export-btn" onClick={handleExportFullReport} disabled={anyLoading}>
                                {isExportingReport ? 'Esportazione...' : 'Esporta Report'}
                            </button>
                        </div>
                    </div>

                    <div className="payroll-table-container">
                        {isLoading ? (
                            <p>Caricamento...</p>
                        ) : summaries.length === 0 ? (
                            <p className="no-data-message">
                                Nessun dato per {selectedMonth.format('MMMM YYYY')}.
                            </p>
                        ) : (
                            <table className="payroll-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Dipendente</th>
                                        <th>Ore Ord.</th>
                                        <th>Eccedenza</th>
                                        <th>Straord.</th>
                                        <th>Pausa Manc.</th>
                                        <th>Ferie</th>
                                        <th>ROL</th>
                                        <th>P104</th>
                                        <th>Malattia</th>
                                        {/* 🆕 Residuo attuale, non legato al mese calcolato */}
                                        <th title="Saldo ferie residuo attuale">Res. Ferie</th>
                                        <th title="Saldo ROL residuo attuale">Res. ROL</th>
                                        <th>Rimb. Spese (€)</th>
                                        <th>Confronto</th>
                                        <th>Stato</th>
                                        <th>Costo (€)</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaries.map(summary => {
                                        const validation = getValidationStatus(summary);
                                        const comparison = getComparison(summary);
                                        const hasAlerts = summary.missingTimelogsAlerts?.length > 0;
                                        const isExpanded = expandedAlerts[summary.id];
                                        const prevData = previousMonthSummaries.find(s => s.userId === summary.userId);

                                        return (
                                            <React.Fragment key={summary.id}>
                                                <tr className={`
                                                    ${hasAlerts ? 'has-alerts' : ''} 
                                                    ${summary.assignmentType === 'backoffice' ? 'backoffice-row' : 'rotation-row'}
                                                    clickable-row
                                                `.trim()}
                                                    onClick={() => setSelectedEmployee(summary)}
                                                    style={{cursor: 'pointer'}}>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <span className={`validation-icon ${validation.class}`} title={validation.label}>
                                                            {validation.icon}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="user-cell">
                                                            <div className="user-name-container">
            <span className="user-name">{summary.userName}</span>
            <span className={`assignment-badge ${summary.assignmentType}`}>
                {summary.assignmentType === 'backoffice' ? '🏢 Backoffice' : '🔄 Turnista'}
            </span>
        </div>

        <div className="alert-badges-container" style={{display: 'flex', gap: '5px', marginTop: '4px'}}>
            
            {/* BADGE 1: Timbrature Mancanti (Triangolo Rosso/Arancio) */}
            {summary.missingTimelogsAlerts?.length > 0 && (
                <button className="alert-badge warning" 
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleAlerts(summary.id); // Espande la riga (opzionale) o apre modale
                    }}
                    title="Timbrature mancanti o anomale">
                    ⚠️ {summary.missingTimelogsAlerts.length}
                </button>
            )}

            {/* BADGE 2: Eccedenza Ore (Orologio Giallo) */}
            {(summary.excessHours > 5) && (
                <span className="alert-badge info" 
                    style={{backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8rem', cursor: 'help'}}
                    title={`Eccedenza elevata: ${summary.excessHours.toFixed(2)}h`}>
                    ⏱️ Eccedenza
                </span>
            )}
        </div>
    </div>
</td>
                                                    <td>{summary.totalWorkedHours?.toFixed(2)}</td>
                                                    <td style={{ 
                                                        color: (summary.excessHours || 0) > 0 ? '#d32f2f' : 'inherit',
                                                        fontWeight: (summary.excessHours || 0) > 0 ? 'bold' : 'normal'
                                                    }}>
                                                        {summary.excessHours?.toFixed(2) || '0.00'}
                                                    </td>
                                                    <td>{summary.overtimeHours?.toFixed(2) || '0.00'}</td>
                                                    <td style={{ color: '#E67E22', fontWeight: '500' }}>
                                                        {summary.missedPauseHours?.toFixed(2) || '0.00'}
                                                    </td>
                                                    <td>{summary.leaveDays?.Ferie || 0}</td>
                                                    <td>{summary.leaveDays?.ROL?.toFixed(2) || '0.00'}</td>
                                                    <td>{summary.leaveDays?.Permesso104?.toFixed(2) || '0.00'}</td>
                                                    <td>{summary.sicknessDays || 0}</td>
                                                    {/* 🆕 Residuo attuale: rosso se negativo (dipendente "in debito") */}
                                                    <td style={{ color: (summary.vacationRemaining ?? 0) < 0 ? '#d32f2f' : 'inherit', fontWeight: 500 }}>
                                                        {summary.vacationRemaining != null ? summary.vacationRemaining.toFixed(2) : '-'}
                                                    </td>
                                                    <td style={{ color: (summary.rolRemaining ?? 0) < 0 ? '#d32f2f' : 'inherit', fontWeight: 500 }}>
                                                        {summary.rolRemaining != null ? summary.rolRemaining.toFixed(2) : '-'}
                                                    </td>
                                                    <td>{summary.totalExpenses?.toFixed(2) || '0.00'}</td>
                                                    <td>
                                                        {comparison && (
                                                            <span className={`comparison ${comparison.diff >= 0 ? 'positive' : 'negative'}`}>
                                                                {comparison.diff >= 0 ? '+' : ''}{comparison.diff.toFixed(1)}h
                                                                <small>({comparison.percentage}%)</small>
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <select value={summary.status || 'calculated'} 
                                                            onChange={(e) => handleStatusChange(summary.id, e.target.value)}
                                                            className={`status-select ${summary.status || 'calculated'}`}>
                                                            <option value="calculated">📊 Calcolato</option>
                                                            <option value="reviewed">👁️ Revisionato</option>
                                                            <option value="approved">✅ Approvato</option>
                                                        </select>
                                                    </td>
                                                    <td style={{backgroundColor: '#e8f5e9', padding: '5px'}} onClick={(e) => e.stopPropagation()}>
                                                        {/* 🆕 Mostra il costo reale se inserito a mano, altrimenti la stima calcolata.
                                                            Resta sempre editabile: quando arriva la busta vera si sovrascrive. */}
                                                        <input type="number" step="0.01" className="cost-input"
                                                            value={
                                                                editedCosts[summary.id] !== undefined
                                                                    ? editedCosts[summary.id]
                                                                    : (summary.realCost != null && summary.realCost !== 0
                                                                        ? summary.realCost
                                                                        : (summary.estimatedTotal || 0))
                                                            }
                                                            onChange={(e) => handleCostChange(summary.id, e.target.value)}
                                                            title={
                                                                summary.realCost != null && summary.realCost !== 0
                                                                    ? 'Costo reale (inserito manualmente)'
                                                                    : `Stima: ${(summary.paidHours || 0).toFixed(2)}h × € ${(summary.hourlyGrossCost || 0).toFixed(2)} + ${summary.taxPercentage || 0}% + spese`
                                                            }
                                                        />
                                                        {(summary.realCost == null || summary.realCost === 0) && (summary.estimatedTotal > 0) && (
                                                            <div style={{ fontSize: '9px', color: '#059669', fontWeight: 700, textAlign: 'center', marginTop: '2px' }}>
                                                                STIMA
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{textAlign: 'center'}} onClick={(e) => e.stopPropagation()}>
                                                        <button disabled={editedCosts[summary.id] === undefined}
                                                            onClick={() => handleSaveRow(summary)}
                                                            className={`save-row-btn ${editedCosts[summary.id] !== undefined ? 'active' : ''}`}>
                                                            Salva
                                                        </button>
                                                    </td>
                                                </tr>
                                                
                                                {hasAlerts && isExpanded && (
                                                  <tr className="alert-row-expanded">
                                                      <td colSpan="17">
                                                            <MissingTimelogsAlert alerts={summary.missingTimelogsAlerts} />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}

            {activeTab === 'doctors' && <DoctorsTally />}

            {selectedEmployee && (
                <EmployeeDetailModal
                    summary={selectedEmployee}
                    previousMonthData={previousMonthSummaries.find(s => s.userId === selectedEmployee.userId)}
                    onClose={() => setSelectedEmployee(null)}
                    onSave={handleSaveNotes}
                />
            )}
        </div>
    );
}

export default PayrollDashboard;